import React, { useEffect, useState, useCallback } from 'react';
import AppShell from '../../components/layout/AppShell';
import { employeeApi, financeApi } from '../../services/api';
import { fmtDT, fmtPKR, ROLE_LABELS, PaymentBadge } from '../../utils/helpers';
import { Spinner, EmptyState, Modal } from '../../components/common';
import toast from 'react-hot-toast';
import { Plus, Search } from 'lucide-react';

// ── Employees List ────────────────────────────────────────────────────────────
export function EmployeesPage() {
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({
    empCode: '', name: '', email: '', mobile: '', password: '',
    designation: '', department: '', role: 'STAFF', employmentType: 'FULL_TIME',
    basicSalary: '', joiningDate: ''
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await employeeApi.getAll({ search: search || undefined, role: roleFilter || undefined });
      if (res.success) setEmployees(res.data);
    } finally { setLoading(false); }
  }, [search, roleFilter]);

  useEffect(() => { const t = setTimeout(load, 300); return () => clearTimeout(t); }, [load]);

  const createEmp = async () => {
    if (!form.empCode || !form.name || !form.email) { toast.error('Fill required fields'); return; }
    try {
      const res = await employeeApi.create(form);
      if (res.success) { toast.success('Employee created'); setShowCreate(false); load(); }
      else throw new Error(res.message);
    } catch (e) { toast.error(e.message || 'Failed'); }
  };

  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));

  return (
    <AppShell title="Employees" actions={
      <button className="btn btn-primary btn-sm" onClick={() => setShowCreate(true)}><Plus size={14} /> Add Employee</button>
    }>
      <div className="filters-bar">
        <div className="search-input">
          <Search size={14} />
          <input placeholder="Search name, code, email…" value={search} onChange={e => setSearch(e.target.value)} />
        </div>
        <select className="form-control" style={{ width: 160 }} value={roleFilter} onChange={e => setRoleFilter(e.target.value)}>
          <option value="">All Roles</option>
          {Object.entries(ROLE_LABELS).filter(([k]) => !['B2C','B2B'].includes(k)).map(([v, l]) =>
            <option key={v} value={v}>{l}</option>
          )}
        </select>
      </div>

      <div className="card">
        <div className="table-wrap">
          {loading ? <Spinner /> : employees.length ? (
            <table>
              <thead><tr><th>Code</th><th>Name</th><th>Role</th><th>Designation</th><th>Hub</th><th>Type</th><th>Joined</th><th>Status</th></tr></thead>
              <tbody>
                {employees.map(e => (
                  <tr key={e.emp_id}>
                    <td><code style={{ fontSize: '.78rem' }}>{e.emp_code}</code></td>
                    <td>
                      <div style={{ fontWeight: 500 }}>{e.name}</div>
                      <div className="text-sm text-muted">{e.email}</div>
                    </td>
                    <td><span className="badge badge-navy">{ROLE_LABELS[e.role] || e.role}</span></td>
                    <td className="text-sm">{e.designation || '—'}</td>
                    <td className="text-sm">{e.hub_name || '—'}</td>
                    <td className="text-sm">{e.employment_type}</td>
                    <td className="text-sm">{e.joining_date ? e.joining_date.slice(0,10) : '—'}</td>
                    <td>
                      <span className={`badge ${e.is_active ? 'badge-success' : 'badge-danger'}`}>
                        {e.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : <EmptyState icon="👥" title="No employees found" />}
        </div>
      </div>

      <Modal open={showCreate} onClose={() => setShowCreate(false)} title="Add New Employee" size="modal-lg">
        <div className="modal-body">
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Employee Code <span className="req">*</span></label>
              <input className="form-control" placeholder="EMP-002" value={form.empCode} onChange={set('empCode')} />
            </div>
            <div className="form-group">
              <label className="form-label">Full Name <span className="req">*</span></label>
              <input className="form-control" placeholder="Muhammad Ali" value={form.name} onChange={set('name')} />
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Email <span className="req">*</span></label>
              <input className="form-control" type="email" placeholder="staff@ecl.pk" value={form.email} onChange={set('email')} />
            </div>
            <div className="form-group">
              <label className="form-label">Mobile</label>
              <input className="form-control" placeholder="+923001234567" value={form.mobile} onChange={set('mobile')} />
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Role <span className="req">*</span></label>
              <select className="form-control" value={form.role} onChange={set('role')}>
                {Object.entries(ROLE_LABELS).filter(([k]) => !['B2C','B2B'].includes(k)).map(([v,l]) =>
                  <option key={v} value={v}>{l}</option>
                )}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Employment Type</label>
              <select className="form-control" value={form.employmentType} onChange={set('employmentType')}>
                <option value="FULL_TIME">Full Time</option>
                <option value="PART_TIME">Part Time</option>
                <option value="CONTRACT">Contract</option>
              </select>
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Designation</label>
              <input className="form-control" placeholder="Branch Manager" value={form.designation} onChange={set('designation')} />
            </div>
            <div className="form-group">
              <label className="form-label">Department</label>
              <input className="form-control" placeholder="Operations" value={form.department} onChange={set('department')} />
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Basic Salary (PKR)</label>
              <input className="form-control" type="number" value={form.basicSalary} onChange={set('basicSalary')} />
            </div>
            <div className="form-group">
              <label className="form-label">Joining Date</label>
              <input className="form-control" type="date" value={form.joiningDate} onChange={set('joiningDate')} />
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Initial Password (default: CP@123456)</label>
            <input className="form-control" type="password" placeholder="Leave blank for default" value={form.password} onChange={set('password')} />
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-outline" onClick={() => setShowCreate(false)}>Cancel</button>
          <button className="btn btn-primary" onClick={createEmp}>Create Employee</button>
        </div>
      </Modal>
    </AppShell>
  );
}

// ── Finance / Invoices ────────────────────────────────────────────────────────
export function InvoicesPage() {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await financeApi.getInvoices({ status: statusFilter || undefined });
      if (res.success) setInvoices(res.data);
    } finally { setLoading(false); }
  }, [statusFilter]);

  useEffect(() => { load(); }, [load]);

  const markPaid = async (invoiceId, total) => {
    try {
      await financeApi.markPaid(invoiceId, { paidAmount: total });
      toast.success('Marked as paid'); load();
    } catch { toast.error('Failed'); }
  };

  return (
    <AppShell title="Invoices">
      <div className="filters-bar">
        <select className="form-control" style={{ width: 160 }} value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
          <option value="">All Statuses</option>
          {['PENDING','PAID','OVERDUE','CANCELLED'].map(s => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>

      <div className="card">
        <div className="table-wrap">
          {loading ? <Spinner /> : invoices.length ? (
            <table>
              <thead><tr><th>Invoice No</th><th>Customer</th><th>Period</th><th>Subtotal</th><th>Tax</th><th>Total</th><th>Due Date</th><th>Status</th><th>Actions</th></tr></thead>
              <tbody>
                {invoices.map(inv => (
                  <tr key={inv.invoice_id}>
                    <td><code style={{ fontSize: '.78rem' }}>{inv.invoice_no}</code></td>
                    <td>
                      <div style={{ fontWeight: 500 }}>{inv.customer_name}</div>
                      <div className="text-sm text-muted">{inv.company_name}</div>
                    </td>
                    <td className="text-sm">{inv.billing_period_start ? `${inv.billing_period_start?.slice(0,10)} – ${inv.billing_period_end?.slice(0,10)}` : '—'}</td>
                    <td>{fmtPKR(inv.subtotal)}</td>
                    <td>{fmtPKR(inv.tax_amount)}</td>
                    <td style={{ fontWeight: 600 }}>{fmtPKR(inv.total)}</td>
                    <td className="text-sm">{inv.due_date?.slice(0,10) || '—'}</td>
                    <td><PaymentBadge status={inv.payment_status} /></td>
                    <td>
                      {inv.payment_status === 'PENDING' && (
                        <button className="btn btn-sm btn-success" onClick={() => markPaid(inv.invoice_id, inv.total)}>Mark Paid</button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : <EmptyState icon="📄" title="No invoices found" />}
        </div>
      </div>
    </AppShell>
  );
}

// ── COD Transactions ──────────────────────────────────────────────────────────
export function CodPage() {
  const [txns, setTxns] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    financeApi.getCod().then(res => { if (res.success) setTxns(res.data); }).finally(() => setLoading(false));
  }, []);

  const deposit = async (codId) => {
    try {
      await financeApi.markCodDeposited(codId, { depositAt: new Date() });
      toast.success('Marked as deposited');
      setTxns(t => t.map(x => x.cod_id === codId ? { ...x, deposit_status: 'DEPOSITED' } : x));
    } catch { toast.error('Failed'); }
  };

  return (
    <AppShell title="COD Transactions">
      <div className="card">
        <div className="table-wrap">
          {loading ? <Spinner /> : txns.length ? (
            <table>
              <thead><tr><th>Tracking ID</th><th>Rider</th><th>Amount Due</th><th>Collected</th><th>Deposit Status</th><th>Collected At</th><th>Actions</th></tr></thead>
              <tbody>
                {txns.map(t => (
                  <tr key={t.cod_id}>
                    <td><code style={{ fontSize: '.78rem' }}>{t.tracking_id}</code></td>
                    <td>{t.rider_name || '—'}</td>
                    <td>{fmtPKR(t.amount_due)}</td>
                    <td style={{ fontWeight: 500 }}>{t.amount_collected ? fmtPKR(t.amount_collected) : '—'}</td>
                    <td>
                      <span className={`badge ${t.deposit_status === 'DEPOSITED' ? 'badge-success' : t.deposit_status === 'SHORTFALL' ? 'badge-danger' : 'badge-warning'}`}>
                        {t.deposit_status}
                      </span>
                    </td>
                    <td className="text-sm">{fmtDT(t.collection_at)}</td>
                    <td>
                      {t.deposit_status === 'PENDING' && (
                        <button className="btn btn-sm btn-success" onClick={() => deposit(t.cod_id)}>Mark Deposited</button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : <EmptyState icon="💵" title="No COD transactions" />}
        </div>
      </div>
    </AppShell>
  );
}
